import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, Check, CheckCircle2, Copy, Eye, Pencil, Plus, Swords, Users } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { api } from '../lib/api'
import type { AdminCompetition, AdminCompetitionSubmission } from '../lib/types'
import { Badge, Button, Card, DateTimeInput, EmptyState, Field, Input, Modal, NumberInput, PageTitle } from '../components/ui'

type CompetitionsResponse = { items: AdminCompetition[]; total: number }
type SubmissionsResponse = { competition_id: string; region_id: string; region_name: string; items: AdminCompetitionSubmission[]; total: number; distinct_work_total: number }

export function CompetitionAdminPage() {
  const qc = useQueryClient()
  const competitions = useQuery({ queryKey: ['admin-competitions'], queryFn: () => api<CompetitionsResponse>('/api/v1/admin/competitions') })
  const [editing, setEditing] = useState<AdminCompetition | null>(null)
  const [creating, setCreating] = useState(false)
  const [detail, setDetail] = useState<AdminCompetition | null>(null)
  const [publishing, setPublishing] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function publish(item: AdminCompetition) {
    const key = `${item.competition_id}::${item.region_id}`
    if (!window.confirm('系统会先按“同一账号 + 标准化作品名”去重，只让最晚投稿参与排名；历史重复投稿保留评分但不公布结果。确认统一发布？')) return
    setPublishing(key)
    setMessage(''); setError('')
    try {
      const result = await api<{ changed: number; status: string }>(`/api/v1/admin/competitions/${encodeURIComponent(item.competition_id)}/${encodeURIComponent(item.region_id)}/publish`, { method: 'POST' })
      setMessage(result.changed > 0 ? `已完成发布处理，共更新 ${result.changed} 条投稿记录` : '该赛区已处于发布状态，无新变更')
      await qc.invalidateQueries({ queryKey: ['admin-competitions'] })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '发布失败')
    } finally {
      setPublishing(null)
    }
  }

  return <div className="workspace-page">
    <PageTitle eyebrow="COMPETITION MANAGEMENT" title="比赛管理" description="创建和管理比赛赛区，查看作品提交统计。" actions={<Button onClick={() => setCreating(true)}><Plus size={15}/>创建比赛</Button>}/>
    {message && <div className="form-success page-message">{message}</div>}{error && <div className="form-error page-message">{error}</div>}
    {competitions.isLoading ? <div className="page-loader">正在读取比赛数据…</div> : !competitions.data?.items.length ? <EmptyState title="暂无比赛" description="创建一个比赛赛区以开始接受投稿。" action={<Button onClick={() => setCreating(true)}><Plus size={15}/>创建比赛</Button>}/> : <div className="list-stack">{competitions.data.items.map(item => <Card className="competition-admin-card" key={`${item.competition_id}::${item.region_id}`}>
      <div className="competition-admin-head">
        <div className="competition-admin-icon"><Swords/></div>
        <div className="competition-admin-info">
          <h3>{item.competition_name || item.competition_id} · {item.name}</h3>
          <p><code>{item.competition_id}</code> · <code>{item.region_id}</code></p>
        </div>
        <div className="competition-admin-badges">
          {(() => { const s = competitionStatus(item); return <span className={`competition-status ${s.className}`}><i className="status-dot"/>{s.label}</span> })()}
        </div>
      </div>
      <div className="competition-admin-stats">
        <div className="stat-item"><Users size={16}/><div><strong>{item.submitter_count}</strong><small>提交人数</small></div></div>
        <div className="stat-item"><Swords size={16}/><div><strong>{item.submission_count}</strong><small>作品数</small></div></div>
        <div className="stat-item"><CalendarDays size={16}/><div><strong>{new Date(item.registration_ends_at).toLocaleDateString('zh-CN')}</strong><small>提交截止</small></div></div>
        <div className="stat-item"><CalendarDays size={16}/><div><strong>{new Date(item.result_publish_at).toLocaleDateString('zh-CN')}</strong><small>结果公布</small></div></div>
      </div>
      <div className="competition-admin-meta">
        <small>静态评分 {Math.round(item.static_weight * 100)}% · 动态评分 {Math.round(item.dynamic_weight * 100)}% · 自建 MCP 额外 +5 · 综合分 /105 · 判定：赛区综合分排名前 {item.finalist_count} 名通过</small>
      </div>
      <div className="competition-admin-actions">
        <Button variant="secondary" onClick={() => setDetail(item)}><Eye size={15}/>查看详情</Button>
        <Button variant="secondary" onClick={() => setEditing(item)}><Pencil size={15}/>编辑</Button>
        <Button variant="secondary" loading={publishing === `${item.competition_id}::${item.region_id}`} onClick={() => void publish(item)}><CheckCircle2 size={15}/>发布结果</Button>
        <CopyLinkButton item={item}/>
      </div>
    </Card>)}</div>}
    {(creating || editing) && <CompetitionModal competition={editing} existing={competitions.data?.items || []} close={() => { setCreating(false); setEditing(null) }} done={() => { setCreating(false); setEditing(null); void qc.invalidateQueries({ queryKey: ['admin-competitions'] }) }}/>}
    {detail && <SubmissionsModal competition={detail} close={() => setDetail(null)}/>}
  </div>
}

/** 复制赛事投稿链接，可直接在投稿页 /submit/competition 中使用（自动锁定该赛区） */
function CopyLinkButton({ item }: { item: AdminCompetition }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    const url = new URL(`/submit/competition?competition=${encodeURIComponent(item.competition_id)}&region=${encodeURIComponent(item.region_id)}`, window.location.href)
    try {
      await navigator.clipboard.writeText(url.href)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch { /* 剪贴板不可用时忽略 */ }
  }
  return <Button variant="secondary" title="复制投稿链接，打开后直接锁定该赛区" onClick={copy}>{copied ? <Check size={15}/> : <Copy size={15}/>}{copied ? '已复制' : '复制链接'}</Button>
}

function SubmissionsModal({ competition, close }: { competition: AdminCompetition; close: () => void }) {
  const submissions = useQuery({
    queryKey: ['admin-competition-submissions', competition.competition_id, competition.region_id],
    queryFn: () => api<SubmissionsResponse>(`/api/v1/admin/competitions/${competition.competition_id}/${competition.region_id}/submissions`),
  })
  return <Modal open onOpenChange={value => !value && close()} title={`投稿详情 · ${competition.name}`} description={`${competition.competition_id} / ${competition.region_id} — 共 ${submissions.data?.total ?? '…'} 条投稿记录 · ${submissions.data?.distinct_work_total ?? '…'} 份作品参与排名`}>
    {submissions.isLoading ? <div className="page-loader">正在读取投稿数据…</div> : !submissions.data?.items.length ? <EmptyState title="还没有投稿" description="该赛区尚未收到任何作品投稿。"/> : <div className="submissions-detail-list">{submissions.data.items.map(item => <div className="submission-detail-row" key={item.id}>
      <div className="submission-detail-top">
        <div className="submission-detail-main">
          <strong>{item.agent_name}</strong>
          <small>v{item.version} · {item.status === 'submitted' ? '已提交' : item.status}</small>
        </div>
        <div className="submission-detail-scores">
          {!item.participates_in_ranking && item.status === 'submitted' && <Badge tone="neutral">已被最新投稿替代</Badge>}
          {item.final_score != null ? <Badge tone="green">{item.final_score.toFixed(1)} / 105 分</Badge> : item.static_score != null ? <Badge tone="blue">静态 {item.static_score.toFixed(1)} / 100</Badge> : <Badge tone="neutral">{item.result_status === 'pending' ? '待评分' : item.result_status}</Badge>}
          {item.final_rank != null && <Badge tone="amber">#{item.final_rank}</Badge>}
        </div>
      </div>
      <div className="submission-detail-author">
        <Users size={14}/>
        <div><strong>{item.author_display_name || item.author_username}</strong><small>@{item.author_username} · {item.team_name}</small></div>
      </div>
      <div className="submission-detail-foot">
        <small>队长：{item.captain_name} · 微信：{item.captain_wechat}</small>
        <small>{item.submitted_at ? new Date(item.submitted_at).toLocaleString('zh-CN', { hour12: false }) : new Date(item.created_at).toLocaleString('zh-CN', { hour12: false })}</small>
      </div>
    </div>)}</div>}
  </Modal>
}

// 常见地区名称 → 拼音映射，用于自动生成赛区 ID
const PINYIN_MAP: Record<string, string> = {
  '华东': 'huadong', '华北': 'huabei', '华南': 'huanan', '华中': 'huazhong',
  '西南': 'xinan', '西北': 'xibei', '东北': 'dongbei',
  '北京': 'beijing', '上海': 'shanghai', '广州': 'guangzhou', '深圳': 'shenzhen', '新加坡': 'singapore',
  '杭州': 'hangzhou', '成都': 'chengdu', '武汉': 'wuhan', '南京': 'nanjing',
  '西安': 'xian', '重庆': 'chongqing', '天津': 'tianjin', '苏州': 'suzhou',
  '长沙': 'changsha', '郑州': 'zhengzhou', '青岛': 'qingdao', '大连': 'dalian',
  '厦门': 'xiamen', '合肥': 'hefei', '济南': 'jinan', '福州': 'fuzhou',
  '昆明': 'kunming', '贵阳': 'guiyang', '兰州': 'lanzhou', '太原': 'taiyuan',
  '沈阳': 'shenyang', '哈尔滨': 'haerbin', '长春': 'changchun', '南昌': 'nanchang',
  '南宁': 'nanning', '海口': 'haikou', '石家庄': 'shijiazhuang', '银川': 'yinchuan',
  '西宁': 'xining', '拉萨': 'lasa', '乌鲁木齐': 'wulumuqi', '呼和浩特': 'huhehaote',
  '浙江': 'zhejiang', '江苏': 'jiangsu', '广东': 'guangdong', '山东': 'shandong',
  '四川': 'sichuan', '湖北': 'hubei', '湖南': 'hunan', '河南': 'henan',
  '河北': 'hebei', '福建': 'fujian', '安徽': 'anhui', '辽宁': 'liaoning',
  '陕西': 'shaanxi', '云南': 'yunnan', '贵州': 'guizhou', '甘肃': 'gansu',
  '山西': 'shanxi', '吉林': 'jilin', '黑龙江': 'heilongjiang', '江西': 'jiangxi',
  '广西': 'guangxi', '海南': 'hainan', '宁夏': 'ningxia', '青海': 'qinghai',
  '新疆': 'xinjiang', '西藏': 'xizang', '内蒙古': 'neimenggu',
}

/** 从赛区名称提取拼音前缀，如 "华东赛区" → "huadong" */
function regionPinyin(name: string): string {
  const cleaned = name.replace(/赛区|分赛|赛点/g, '').trim()
  for (const [cn, py] of Object.entries(PINYIN_MAP)) { if (cleaned.includes(cn)) return py }
  return ''
}

/** 自动生成赛区 ID：拼音+数字，如 huadong1 */
function autoRegionId(name: string, existing: AdminCompetition[], competitionId: string): string {
  const prefix = regionPinyin(name)
  if (!prefix) return ''
  const used = existing.filter(c => c.competition_id === competitionId).map(c => c.region_id)
  for (let n = 1; n <= 99; n++) { if (!used.includes(`${prefix}${n}`)) return `${prefix}${n}` }
  return `${prefix}${Date.now() % 1000}`
}

function autoCompetitionId(): string {
  return `avenger-x-${new Date().getFullYear()}`
}

function CompetitionModal({ competition, existing, close, done }: { competition: AdminCompetition | null; existing: AdminCompetition[]; close: () => void; done: () => void }) {
  const isEdit = !!competition
  const [competitionId, setCompetitionId] = useState(competition?.competition_id || autoCompetitionId())
  const [regionId, setRegionId] = useState(competition?.region_id || '')
  const [regionTouched, setRegionTouched] = useState(false)
  const [competitionName, setCompetitionName] = useState(competition?.competition_name || '')
  const [name, setName] = useState(competition?.name || '')
  const [registrationStartsAt, setRegistrationStartsAt] = useState(competition?.registration_starts_at ? toLocalInput(competition.registration_starts_at) : '')
  const [registrationEndsAt, setRegistrationEndsAt] = useState(competition ? toLocalInput(competition.registration_ends_at) : '')
  const [resultPublishAt, setResultPublishAt] = useState(competition ? toLocalInput(competition.result_publish_at) : '')
  const [staticWeight, setStaticWeight] = useState(String(competition?.static_weight ?? 0.5))
  const [dynamicWeight, setDynamicWeight] = useState(String(competition?.dynamic_weight ?? 0.5))
  const [finalistCount, setFinalistCount] = useState(String(competition?.finalist_count ?? 15))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const idCandidates = isEdit
    ? existing.filter(item => item.competition_id !== competition.competition_id || item.region_id !== competition.region_id)
    : existing

  function onCompetitionIdChange(value: string) {
    setCompetitionId(value)
    if (!regionTouched && name) {
      const auto = autoRegionId(name, idCandidates, value)
      if (auto) setRegionId(auto)
    }
  }

  function onNameChange(value: string) {
    setName(value)
    if (!regionTouched) {
      const auto = autoRegionId(value, idCandidates, competitionId)
      if (auto) setRegionId(auto)
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError('')
    const sw = parseFloat(staticWeight)
    const dw = parseFloat(dynamicWeight)
    if (Number.isNaN(sw) || Number.isNaN(dw) || sw < 0 || dw < 0 || Math.abs(sw + dw - 1) > 0.001) {
      setError('静态权重与动态权重之和必须为 1'); setBusy(false); return
    }
    try {
      const registrationStartsAtIso = parseLocalDateTime(registrationStartsAt, '提交开始时间', false)
      const registrationEndsAtIso = parseLocalDateTime(registrationEndsAt, '提交截止时间', true)
      const resultPublishAtIso = parseLocalDateTime(resultPublishAt, '结果公布时间', true)
      const endpoint = competition
        ? `/api/v1/admin/competitions/${encodeURIComponent(competition.competition_id)}/${encodeURIComponent(competition.region_id)}`
        : '/api/v1/admin/competitions'
      await api(endpoint, { method: 'PUT', body: {
        competition_id: competitionId,
        region_id: regionId,
        competition_name: competitionName,
        name,
        timezone: 'Asia/Shanghai',
        registration_starts_at: registrationStartsAtIso,
        registration_ends_at: registrationEndsAtIso,
        result_publish_at: resultPublishAtIso,
        static_weight: sw,
        dynamic_weight: dw,
        finalist_count: parseInt(finalistCount),
      } })
      done()
    } catch (reason) { setError(reason instanceof Error ? reason.message : '操作失败') } finally { setBusy(false) }
  }

  return <Modal className="competition-config-dialog" open onOpenChange={value => !value && close()} title={isEdit ? '编辑比赛' : '创建比赛'} description={isEdit ? '修改比赛赛区配置。' : '创建新的比赛赛区，创建后用户即可在赛事投稿中选择。'}>
    <form className="stack" onSubmit={submit}>
      <Field label="比赛名称" required hint="用户在投稿页面看到的比赛名"><Input value={competitionName} onChange={e => setCompetitionName(e.target.value)} placeholder="例如：Avenger X 2026 大赛" required/></Field>
      <Field label="赛区（地点）" required hint="赛区代表地点，如北京、新加坡、上海"><Input value={name} onChange={e => onNameChange(e.target.value)} placeholder="例如：北京" required/></Field>
      <div className="form-grid">
        <Field label="比赛 ID" required hint="自动生成，可修改"><Input value={competitionId} onChange={e => onCompetitionIdChange(e.target.value)} required/></Field>
        <Field label="赛区 ID" required hint={regionId ? '根据赛区名称自动生成，可修改' : '输入赛区名称后自动生成'}><Input value={regionId} onChange={e => { setRegionId(e.target.value); setRegionTouched(true) }} required placeholder="如 beijing1"/></Field>
      </div>
      <div className="form-grid">
        <Field label="提交开始时间" hint="留空表示不限制，创建后即可提交"><DateTimeInput value={registrationStartsAt} onChange={setRegistrationStartsAt}/></Field>
        <Field label="提交截止时间" required><DateTimeInput value={registrationEndsAt} onChange={setRegistrationEndsAt} required/></Field>
        <Field label="结果公布时间" required><DateTimeInput value={resultPublishAt} onChange={setResultPublishAt} required/></Field>
      </div>
      <div className="form-grid">
        <Field label="静态评分权重" hint="填写 0~1 之间的小数，例如 0.5 表示 50%"><NumberInput decimal value={staticWeight} onChange={e => setStaticWeight(e.target.value)} placeholder="0.5"/></Field>
        <Field label="动态评分权重" hint="与静态评分权重之和必须为 1"><NumberInput decimal value={dynamicWeight} onChange={e => setDynamicWeight(e.target.value)} placeholder="0.5"/></Field>
      </div>
      <div className="form-grid">
        <Field label="入围名额" hint="通过/未通过按赛区综合分排名判定：排名前 N 名通过，其余未通过"><NumberInput value={finalistCount} onChange={e => setFinalistCount(e.target.value)} placeholder="15"/></Field>
      </div>
      {error && <div className="form-error">{error}</div>}
      <Button type="submit" loading={busy}>{isEdit ? '保存修改' : '创建比赛'}</Button>
    </form>
  </Modal>
}

function parseLocalDateTime(value: string, label: string, required: boolean): string | null {
  if (!value) {
    if (required) throw new Error(`${label}不能为空`)
    return null
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value)
  if (!match) throw new Error(`${label}格式不正确，请填写 YYYY-MM-DD HH:mm`)
  const [, yearText, monthText, dayText, hourText, minuteText] = match
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const hour = Number(hourText)
  const minute = Number(minuteText)
  const date = new Date(year, month - 1, day, hour, minute)
  if (
    month < 1 || month > 12 ||
    day < 1 || day > 31 ||
    hour > 23 || minute > 59 ||
    date.getFullYear() !== year || date.getMonth() !== month - 1 ||
    date.getDate() !== day || date.getHours() !== hour || date.getMinutes() !== minute
  ) throw new Error(`${label}不是有效的日期时间`)
  return date.toISOString()
}

function toLocalInput(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function competitionStatus(item: AdminCompetition): { label: string; className: string } {
  const now = new Date()
  if (item.registration_starts_at && new Date(item.registration_starts_at) > now) {
    return { label: '未开始', className: 'is-pending' }
  }
  if (new Date(item.registration_ends_at) > now) {
    return { label: '提交中', className: 'is-open' }
  }
  return { label: '已截止', className: 'is-closed' }
}
